import { useEffect, useState } from "react";
import { CONTACT_PERMISSIONS_ALL_FALSE, CONTACT_PERMISSIONS_ALL_TRUE } from "@constants";
import type { UseFormMethods } from "react-hook-form";
import { ICustomerDetailsForm, IPermissionNameValue } from "@features/customerDetails/ICustomerDetailsForm";

interface IParams {
    formMethods: UseFormMethods<ICustomerDetailsForm>;
}

export const useMarketingPreferences = (props: IParams) => {
    const { formMethods } = props;
    const { setValue, watch } = formMethods;
    const { allowThirdParty, noLongerCustomer, activeCustomer } = watch("contactPermissions");
    const isAtLeastOnePermissionEnabled = (permissions: IPermissionNameValue): boolean =>
        Object.entries(permissions).some(([, value]) => value === true);

    const [isExistingCustomerCollapsableOpened, setIsExistingCustomerCollapsableOpened] = useState(
        isAtLeastOnePermissionEnabled(activeCustomer)
    );
    const [isNoLongerCustomerCollapsableOpened, setIsNoLongerCustomerCollapsableOpened] = useState(
        isAtLeastOnePermissionEnabled(noLongerCustomer)
    );
    const [isAllowThirdPartyCollapsableOpened, setIsAllowThirdPartyCollapsableOpened] = useState(
        isAtLeastOnePermissionEnabled(allowThirdParty)
    );

    const handleExistingCustomerToggleChange = (isCollapseOpened: boolean) => {
        setIsExistingCustomerCollapsableOpened((prevState) => !prevState);
        isCollapseOpened
            ? setValue("contactPermissions.activeCustomer", CONTACT_PERMISSIONS_ALL_TRUE.activeCustomer)
            : setValue("contactPermissions.activeCustomer", CONTACT_PERMISSIONS_ALL_FALSE.activeCustomer);
    };

    const handleNoLongerCustomerToggleChange = (isCollapseOpened: boolean) => {
        setIsNoLongerCustomerCollapsableOpened((prevState) => !prevState);
        isCollapseOpened
            ? setValue("contactPermissions.noLongerCustomer", CONTACT_PERMISSIONS_ALL_TRUE.noLongerCustomer)
            : setValue("contactPermissions.noLongerCustomer", CONTACT_PERMISSIONS_ALL_FALSE.noLongerCustomer);
    };

    const handleAllowThirdPartyToggleChange = (isCollapseOpened: boolean) => {
        setIsAllowThirdPartyCollapsableOpened((prevState) => !prevState);
        isCollapseOpened
            ? setValue("contactPermissions.allowThirdParty", CONTACT_PERMISSIONS_ALL_TRUE.allowThirdParty)
            : setValue("contactPermissions.allowThirdParty", CONTACT_PERMISSIONS_ALL_FALSE.allowThirdParty);
    };

    useEffect(() => {
        if (Object.entries(activeCustomer).every(([, value]) => value === false)) {
            setIsExistingCustomerCollapsableOpened(false);
        }
    }, [activeCustomer]);

    useEffect(() => {
        if (Object.entries(noLongerCustomer).every(([, value]) => value === false)) {
            setIsNoLongerCustomerCollapsableOpened(false);
        }
    }, [noLongerCustomer]);

    useEffect(() => {
        if (Object.entries(allowThirdParty).every(([, value]) => value === false)) {
            setIsAllowThirdPartyCollapsableOpened(false);
        }
    }, [allowThirdParty]);

    return {
        isExistingCustomerCollapsableOpened,
        isNoLongerCustomerCollapsableOpened,
        isAllowThirdPartyCollapsableOpened,
        handleExistingCustomerToggleChange,
        handleNoLongerCustomerToggleChange,
        handleAllowThirdPartyToggleChange,
    };
};
